import {
	Shop,
	ContractEmailData,
	ShopPublicInfo,
	Category,
	OrderObject,
	CategoryWithTerms,
} from 'common/types';
import { getResponsiblePersonDetails } from 'services/utils';
import { getCategoriesWithTermsFromShoppers } from 'common/utils/rentals';
import { getDefaultDateObject } from 'common/utils/dateUtils';
import { t } from 'services/localization/i18n';
import errorHandler from 'common/services/errorHandler';
import { httpsCallable } from 'common/frontend/firebase/functions';
import { createShopperListForEmail } from 'common/modules/emails/utils/in-store';
import { urlToDataUri } from 'common/modules/emails/utils/urlToDataUri';

export const sendContract = (
	activeStoreRental: OrderObject,
	activeShop: Shop | undefined,
	categories: Category[],
) => {
	const sendContractCloudFunction = httpsCallable<any, any>('http-emailInStoreContract');
	if (activeShop && activeShop.publicInfo) {
		getContractEmailData(
			activeStoreRental,
			activeShop.publicInfo,
			categories,
			async (emailData) => {
				if (emailData) {
					try {
						await sendContractCloudFunction(emailData);
					} catch (e) {
						errorHandler.report(e);
					}
				}
			},
		);
	}
};

const getContractEmailData = async (
	activeStoreRental: OrderObject,
	shopPublicInfo: ShopPublicInfo,
	categories: Category[],
	callback: (emailData: ContractEmailData | null) => void,
) => {
	const { rentalInfo, shoppers, products } = activeStoreRental;
	const responsiblePerson = getResponsiblePersonDetails(rentalInfo.responsiblePerson, shoppers);
	if (!responsiblePerson) {
		callback(null);
		return;
	}
	let recipientEmail = responsiblePerson.email;
	const language = responsiblePerson.language === 'fi' ? 'fi' : 'en';
	const shoppersData = createShopperListForEmail(
		shoppers,
		products,
		categories,
		shopPublicInfo.dateFormat ?? getDefaultDateObject(),
		t,
		language,
	);
	if (!recipientEmail) {
		recipientEmail =
			prompt('Email not found, please enter email for sending rental contract:', 'Email') || '';
	}
	const emailData: ContractEmailData = {
		recipientEmail,
		responsiblePerson,
		language,
		shopPublicInfo,
		shoppersData,
		locationId: activeStoreRental.rentalInfo.startLocation.id,
		termsPDFs: [],
		generalTermsPDF: '',
	};
	const categoriesWithTerms = getCategoriesWithTermsFromShoppers(
		shoppers,
		categories,
		shopPublicInfo?.termsOfService ?? null,
		language,
		t,
		!!shopPublicInfo.legacySettings?.useCategoryTermsOverwrite,
	);

	const termsPDFs = await getOrderTerms(categoriesWithTerms);

	callback({ ...emailData, termsPDFs });
};

const getOrderTerms = async (categoriesWithTerms: CategoryWithTerms[]) =>
	await Promise.all(
		categoriesWithTerms.map(async (categoryWithTerms) => ({
			name: categoryWithTerms.name,
			content: await urlToDataUri(categoryWithTerms.terms.url),
		})),
	);
